<template>
  <nav class="breadcrumb-container" v-if="breadcrumbs.length > 0">
    <!-- Mobile: Simplified back navigation -->
    <div class="breadcrumb-mobile" v-if="isMobile">
      <button @click="goBack" class="back-button">
        <span class="back-icon">←</span>
        <span class="back-text">{{ getBackText() }}</span>
      </button>
    </div>
    
    <!-- Desktop: Full breadcrumb trail -->
    <ol class="breadcrumb-list" v-else>
      <li 
        v-for="(breadcrumb, index) in breadcrumbs" 
        :key="index"
        class="breadcrumb-item"
      >
        <router-link 
          v-if="breadcrumb.path && index < breadcrumbs.length - 1"
          :to="breadcrumb.path"
          class="breadcrumb-link"
        >
          {{ breadcrumb.label }}
        </router-link>
        <span 
          v-else 
          class="breadcrumb-current"
          :class="{ 'breadcrumb-last': index === breadcrumbs.length - 1 }"
        >
          {{ breadcrumb.label }}
        </span>
        <span 
          v-if="index < breadcrumbs.length - 1" 
          class="breadcrumb-separator"
        >
          /
        </span>
      </li>
    </ol>
  </nav>
</template>

<script>
export default {
  name: 'BreadcrumbNavigation',
  props: {
    breadcrumbs: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isMobile: false
    };
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    goBack() {
      if (this.breadcrumbs.length > 1) {
        const previousBreadcrumb = this.breadcrumbs[this.breadcrumbs.length - 2];
        if (previousBreadcrumb && previousBreadcrumb.path) {
          this.$router.push(previousBreadcrumb.path);
        } else {
          // Fallback to home
          this.$router.push('/');
        }
      } else {
        // Fallback to home
        this.$router.push('/');
      }
    },
    getBackText() {
      if (this.breadcrumbs.length > 1) {
        const previousBreadcrumb = this.breadcrumbs[this.breadcrumbs.length - 2];
        return previousBreadcrumb ? previousBreadcrumb.label : 'Back';
      }
      return 'Back';
    }
  }
};
</script>

<style scoped>
.breadcrumb-container {
  margin: 15px 0;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  border-left: 4px solid #4C6B30;
}

.breadcrumb-list {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
  line-height: 1.2;
}

.breadcrumb-link {
  color: #4C6B30;
  text-decoration: none;
  transition: all 0.3s ease;
  font-weight: 500;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.breadcrumb-link:hover {
  color: #6E9F49;
  text-decoration: none;
  background-color: #e8f5e8;
}

.breadcrumb-current {
  color: #333;
  font-weight: 500;
  font-size: 12px;
}

.breadcrumb-last {
  color: #666;
  font-weight: normal;
}

.breadcrumb-separator {
  margin: 0 6px;
  color: #999;
  font-weight: normal;
  font-size: 11px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .breadcrumb-container {
    padding: 10px 15px;
    margin: 15px 0;
    border-radius: 6px;
  }
  
  .breadcrumb-list {
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .breadcrumb-item {
    font-size: 13px;
    line-height: 1.3;
  }
  
  .breadcrumb-link {
    padding: 2px 6px;
    font-size: 12px;
  }
  
  .breadcrumb-separator {
    margin: 0 3px;
    font-size: 12px;
  }
}

/* Mobile breadcrumb styles */
.breadcrumb-mobile {
  display: none;
}

@media (max-width: 768px) {
  .breadcrumb-mobile {
    display: block;
  }
  
  .breadcrumb-list {
    display: none;
  }
  
  .back-button {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    color: #4C6B30;
    font-size: 16px;
    font-weight: 500;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;
    justify-content: flex-start;
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
  }
  
  .back-button:hover {
    background-color: #e8f5e8;
    color: #6E9F49;
  }
  
  .back-button:active {
    background-color: #d4edda;
    transform: scale(0.98);
  }
  
  .back-icon {
    font-size: 18px;
    margin-right: 8px;
    font-weight: bold;
  }
  
  .back-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .breadcrumb-container {
    padding: 8px 12px;
    margin: 10px 0;
    background-color: transparent;
    border: none;
    box-shadow: none;
  }
  
  .back-button {
    padding: 10px 12px;
    font-size: 14px;
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .back-icon {
    font-size: 16px;
    margin-right: 6px;
  }
  
  .back-text {
    font-size: 13px;
  }
}
</style> 